import React, { useState, SyntheticEvent, Fragment } from 'react';
import { Category, LinkType } from 'src/common/explorer/lib/types';
import ProductDetail from './ProductDetail';
import { FormattedMessage, MessageDescriptor } from 'react-intl';
import { ReactComponent as LoggingAndMonitoringIcon } from '../assets/images/icons/logging-and-monitoring.svg';
import { ReactComponent as ContainersIcon } from '../assets/images/icons/containers.svg';
import { ReactComponent as QuantumIcon } from '../assets/images/icons/quantum.svg';
import { ReactComponent as AutomationIcon } from '../assets/images/icons/ibm--automation-platform-clean.svg';
import { ReactComponent as AiIcon } from '../assets/images/icons/ai.svg';
import { ReactComponent as AnalyticsIcon } from '../assets/images/icons/analytics.svg';
import { ReactComponent as ComputeIcon } from '../assets/images/icons/compute-and-servers.svg';
import { ReactComponent as BlockchainIcon } from '../assets/images/icons/blockchain--02.svg';
import { ReactComponent as DatabasesIcon } from '../assets/images/icons/database.svg';
import { ReactComponent as DeveloperToolsIcon } from '../assets/images/icons/developer-tools.svg';
import { ReactComponent as IotIcon } from '../assets/images/icons/internet-of-things.svg';
import { ReactComponent as IntegrationIcon } from '../assets/images/icons/integration--02.svg';
import { ReactComponent as NetworkingIcon } from '../assets/images/icons/network.svg';
import { ReactComponent as SecurityIcon } from '../assets/images/icons/security.svg';
import { ReactComponent as StorageIcon } from '../assets/images/icons/storage.svg';
import { useProductExplorerSelector } from '../lib/redux/hooks';
import { ChevronDown32, ChevronUp32 } from '@carbon/icons-react';
import './ProductsDisplay.scss';
import { CarbonThemes } from 'src/types/carbon';

interface ProductsDisplayProps {
  linkType: string;
}

const ProductsDisplay: React.FC<ProductsDisplayProps> = ({ linkType }) => {
  const [selectedCategory, setSelectedCategory] = useState('');
  const categories = useProductExplorerSelector((state) => state.categories);
  const messages = useProductExplorerSelector<
    Record<string, MessageDescriptor>
  >((state) => state.messages);
  const theme = useProductExplorerSelector<CarbonThemes>(
    (state) => state.theme,
  );

  function handleInteraction(e: SyntheticEvent<HTMLDivElement>) {
    if (selectedCategory === e.currentTarget.dataset.category) {
      setSelectedCategory('');
    } else {
      setSelectedCategory(e.currentTarget.dataset.category || '');
    }
  }

  function handleKeyPress(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      handleInteraction(e);
    }
  }

  const addIconColorClass = theme === CarbonThemes.WHITE ? 'icon-black' : '';

  function generateIconMarkup(categoryIcon: string, categoryName: string) {
    if (categoryIcon.includes('containers')) {
      return (
        <ContainersIcon
          className={`icon ${addIconColorClass}`}
          aria-hidden="true"
        />
      );
    } else if (categoryIcon.includes('logging-and-monitoring')) {
      return (
        <LoggingAndMonitoringIcon
          className={`icon ${addIconColorClass}`}
          aria-hidden="true"
        />
      );
    } else if (categoryIcon.includes('automation')) {
      return (
        <AutomationIcon
          className={`icon ${addIconColorClass}`}
          aria-hidden="true"
        />
      );
    } else if (categoryIcon.includes('product-ai')) {
      return (
        <AiIcon className={`icon ${addIconColorClass}`} aria-hidden="true" />
      );
    } else if (categoryIcon.includes('blockchain')) {
      return (
        <BlockchainIcon
          className={`icon ${addIconColorClass}`}
          aria-hidden="true"
        />
      );
    } else if (categoryIcon.includes('compute')) {
      return (
        <ComputeIcon
          className={`icon ${addIconColorClass}`}
          aria-hidden="true"
        />
      );
    } else if (categoryIcon.includes('analytics')) {
      return (
        <AnalyticsIcon
          className={`icon ${addIconColorClass}`}
          aria-hidden="true"
        />
      );
    } else if (categoryIcon.includes('databases')) {
      return (
        <DatabasesIcon
          className={`icon ${addIconColorClass}`}
          aria-hidden="true"
        />
      );
    } else if (categoryIcon.includes('developer-tools')) {
      return (
        <DeveloperToolsIcon
          className={`icon ${addIconColorClass}`}
          aria-hidden="true"
        />
      );
    } else if (categoryIcon.includes('internet-of-things')) {
      return (
        <IotIcon className={`icon ${addIconColorClass}`} aria-hidden="true" />
      );
    } else if (categoryIcon.includes('integration')) {
      return (
        <IntegrationIcon
          className={`icon ${addIconColorClass}`}
          aria-hidden="true"
        />
      );
    } else if (categoryIcon.includes('networking')) {
      return (
        <NetworkingIcon
          className={`icon ${addIconColorClass}`}
          aria-hidden="true"
        />
      );
    } else if (categoryIcon.includes('security')) {
      return (
        <SecurityIcon
          className={`icon ${addIconColorClass}`}
          aria-hidden="true"
        />
      );
    } else if (categoryIcon.includes('storage')) {
      return (
        <StorageIcon
          className={`icon ${addIconColorClass}`}
          aria-hidden="true"
        />
      );
    } else if (categoryIcon.includes('quantum')) {
      return (
        <QuantumIcon
          className={`icon ${addIconColorClass}`}
          aria-hidden="true"
        />
      );
    } else {
      return (
        <div>
          <img
            className={`icon ${addIconColorClass}`}
            aria-hidden="true"
            src={categoryIcon}
            alt={`Icon for ${categoryName}`}
          />
        </div>
      );
    }
  }

  function shouldCategoryShow(category: Category) {
    // if all products within a category lack pricing urls, don't show category
    if (linkType === LinkType.product) {
      return true;
    }

    return category.products.some((product) => product.pricingUrl !== null);
  }

  return (
    <div className="product-display">
      {categories.filter(shouldCategoryShow).map((category, i, categories) => {
        return (
          <Fragment key={`category-${i}`}>
            <div
              className="product-display__tile-wrapper"
              onKeyPress={handleKeyPress}
              onClick={handleInteraction}
              data-category={category.name}
              data-i={i}
              tabIndex={0}
            >
              <div
                className={`product-display__tile ${
                  selectedCategory === category.name ? 'selected' : ''
                }`}
              >
                {generateIconMarkup(category.icon, category.name)}
                <div className="product-display__name-chevron">
                  <h4 className="product-display__name">
                    <FormattedMessage
                      {...messages[`${category.translationId}Name`]}
                    />
                  </h4>
                  <span>
                    {selectedCategory === category.name ? (
                      <ChevronUp32 />
                    ) : (
                      <ChevronDown32 />
                    )}
                  </span>
                </div>
              </div>
            </div>
            <ProductDetail
              category={categories[i]}
              products={categories[i].products}
              index={i}
              selected={categories[i].name === selectedCategory}
              linkType={linkType}
            />
          </Fragment>
        );
      })}
    </div>
  );
};

export default ProductsDisplay;
